import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as R from 'ramda';
import PropTypes from 'prop-types';
import { DrawerLayoutWrapper } from 'common';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { createSnack } from 'snackbar/snackbarSlice';
import { complement, isNil, pickBy } from 'ramda';
import { useGetLocationsInventoryOriginsQuery } from '../../reconsignmentsEndpoints';

const styles = {
  caption: {
    color: 'var(--Text-Grey-Medium, #666)',
    fontFamily: '"Gothic A1"',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 600,
    letterSpacing: '0.4px',
    lineHeight: '16px',
  },
  subtitle: {
    color: 'var(--Text-Grey-Dark, #213657)',
    fontFamily: '"Gothic A1"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 700,
    letterSpacing: '0.5px',
    lineHeight: '24px',
  },
};

function DetailField({ label, value, isLast = false }) {
  return (
    <>
      <Typography sx={styles.caption}>{label}</Typography>
      <Typography sx={{ ...styles.subtitle, mb: isLast ? 0 : 2 }}>{value}</Typography>
    </>
  );
}

DetailField.propTypes = {
  isLast: PropTypes.bool,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
DetailField.defaultProps = {
  isLast: false,
};

export default function LocationDetailsDrawer({
  open,
  onClose,
  showCheckboxes,
  location,
  reconsignmentState,
  showSubmitButton,
  selectedOrigin,
  setSelectedOrigin,
  setReconsignmentState,
}) {
  // Early return if no location - prevents null reference errors
  if (!location) {
    return null;
  }

  const dispatch = useDispatch();
  const [selectedOrigins, setSelectedOrigins] = useState(
    selectedOrigin ? selectedOrigin.map(origin => origin.originId) : []
  );

  const queryParams = useMemo(() => {
    const NULL_LIKE_VALUES = ['ANY', 'ALL', '', null, undefined];
    const getNullOrValue = val =>
      R.includes(R.toUpper(String(val ?? '')), NULL_LIKE_VALUES) ? null : val;

    const raw = {
      consignmentStatus: getNullOrValue(R.prop('consignmentStatus', reconsignmentState)),
      inventoryOwner: {
        companyCustomerIdName: R.path(['inventoryOwner', 'customerIdName'], reconsignmentState),
        dataCompanyId: R.path(['inventoryOwner', 'dataCompanyId'], reconsignmentState),
      },
      inventoryStatus: getNullOrValue(R.prop('inventoryStatus', reconsignmentState)),
      locationId: R.prop('locationId', location),
      productId: R.path(['product', 'productId'], reconsignmentState),
      rvp: getNullOrValue(R.prop('rvp', reconsignmentState)),
    };
    return pickBy(complement(isNil), raw);
  }, [reconsignmentState, location]);

  const { data: locationsInventoryOrigins = [], error } = useGetLocationsInventoryOriginsQuery({
    params: queryParams,
  });

  useEffect(() => {
    if (error) {
      dispatch(
        createSnack({
          message: error?.data?.title,
          options: { variant: 'error' },
        })
      );
    }
  }, [dispatch, error]);

  const {
    inventoryOwner = '',
    product = '',
    rvp = 'Any',
    locationIdn = '',
    locationName = '',
  } = location;

  const originsTotalVolume = locationsInventoryOrigins?.totalVolume
    ? `${locationsInventoryOrigins.totalVolume.toLocaleString()} bbls`
    : 'N/A';

  const formattedLocation =
    locationIdn && locationName
      ? `${locationIdn} ${locationName}`
      : locationName || locationIdn || 'N/A';

  const handleOriginToggle = originId => {
    setSelectedOrigins(prevSelected =>
      prevSelected.includes(originId)
        ? prevSelected.filter(id => id !== originId)
        : [...prevSelected, originId]
    );
  };

  const handleSubmitOrigins = () => {
    const selectedObjs = (locationsInventoryOrigins?.origins || []).filter(o =>
      selectedOrigins.includes(o.originId)
    );

    setSelectedOrigin(selectedObjs);

    setReconsignmentState(prev => ({
      ...prev,
      origin: selectedObjs,
    }));

    onClose();
  };

  const footer = showSubmitButton ? (
    <Button color="primary" fullWidth onClick={handleSubmitOrigins} variant="contained">
      Submit
    </Button>
  ) : (
    <Button color="primary" fullWidth onClick={onClose} variant="outlined">
      Close
    </Button>
  );

  const content = (
    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
      <DetailField label="Inventory Owner" value={inventoryOwner || 'N/A'} />
      <DetailField label="Location" value={formattedLocation} />
      <DetailField label="Product" value={product || 'N/A'} />
      <DetailField label="RVP" value={rvp || 'Any'} />
      <DetailField isLast label="Volume" value={originsTotalVolume} />
      <Divider sx={{ my: 2 }} />
      {locationsInventoryOrigins.origins?.length > 0
        ? R.prop('origins', locationsInventoryOrigins).map(origin => (
            <Box key={R.prop('originId', origin)} sx={{ mb: 2 }}>
              {showCheckboxes ? (
                <Box
                  key={R.prop('originId', origin)}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    mb: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Box sx={{ flexShrink: 0 }}>
                        <Checkbox
                          checked={selectedOrigins.includes(R.prop('originId', origin))}
                          onChange={() => handleOriginToggle(R.prop('originId', origin))}
                        />
                      </Box>
                    }
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <Typography sx={{ pb: '4px' }} variant="body1">
                          {R.prop('originName', origin)}
                        </Typography>
                        <Typography sx={{ color: '#666666' }} variant="body2">
                          {Number(R.prop('volume', origin)).toLocaleString()} bbls
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ) : (
                <>
                  <Typography sx={{ pb: '4px' }} variant="body1">
                    {R.prop('originName', origin)}
                  </Typography>
                  <Typography sx={{ color: '#666666' }} variant="body2">
                    {Number(R.prop('volume', origin)).toLocaleString()} bbls
                  </Typography>
                </>
              )}
              <Divider sx={{ my: 2 }} />
            </Box>
          ))
        : null}
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      slotProps={{
        paper: { sx: { display: 'flex', flexDirection: 'column', width: 360 } },
      }}
    >
      <DrawerLayoutWrapper buttons={footer} onHeaderButtonClick={onClose} title="Location Details">
        {content}
      </DrawerLayoutWrapper>
    </Drawer>
  );
}

LocationDetailsDrawer.propTypes = {
  location: PropTypes.shape({
    inventoryOwner: PropTypes.string,
    locationIdn: PropTypes.string,
    locationName: PropTypes.string,
    product: PropTypes.string,
    rvp: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  reconsignmentState: PropTypes.shape({
    inventoryOwner: PropTypes.shape({}),
    product: PropTypes.shape({}),
    rvp: PropTypes.string,
  }).isRequired,
  selectedOrigin: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setReconsignmentState: PropTypes.func.isRequired,
  setSelectedOrigin: PropTypes.func.isRequired,
  showCheckboxes: PropTypes.bool,
  showSubmitButton: PropTypes.bool,
};

LocationDetailsDrawer.defaultProps = {
  location: null,
  showCheckboxes: false,
  showSubmitButton: false,
};
